import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";

import {EMPTY, forkJoin} from "rxjs";
import {first, switchMap, take} from "rxjs/operators";
import * as L from "leaflet";
import {
  Circle,
  circle,
  divIcon,
  LatLng, LatLngBounds, LatLngLiteral,
  marker,
  Marker,
  polygon,
  Polygon,
  rectangle,
  Rectangle,
  Tooltip,
  tooltip
} from "leaflet";
import { TranslocoService } from "@jsverse/transloco";

import { LorenMap } from "../../../loren-map/constants/loren-map.constants";
import { LorenGeofencingCircle } from "../../interfaces/loren-geofencing-circle.interface";
import {
  LorenNavService
} from "../../../../shared/modules/loren-nav/components/loren-nav/services/loren-nav-service/loren-nav.service";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import {
  LorenGeofencingCircleService
} from "../../services/loren-geofencing-circle-service/loren-geofencing-circle.service";
import {
  LorenGeofencingPolygonService
} from "../../services/loren-geofencing-polygon-service/loren-geofencing-polygon.service";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenGeofencingResolver } from "../../constants/loren-geofencing-resolver.constants";
import { LorenModalData } from "../../../../shared/modules/loren-dialog/interfaces/loren-modal-data.interface";
import {
  LorenDialogComponent
} from "../../../../shared/modules/loren-dialog/components/loren-dialog/loren-dialog.component";
import { LorenFieldTypes } from "../../../../shared/modules/loren-entity-form-card/enums/loren-field-types.enum";
import {
  LorenEntityFormModel
} from "../../../../shared/modules/loren-entity-form-card/interfaces/loren-entity-form-model.interface.ts";
import {
  LorenShapeCreationDialogComponent
} from "../loren-shape-creation-dialog/loren-shape-creation-dialog.component";
import { LorenGeofencingShape } from "../../interfaces/loren-geofencing-shape.interface";
import { LorenGeofencingType } from "../../enums/loren-geofencing-type.enum";
import { LorenGeofencingPolygon } from "../../interfaces/loren-geofencing-polygon.interface";
import { LorenGeofencingShapes } from "../../constants/loren-geofencing-shapes.constants";
import { LorenGeofencingZone } from "../../interfaces/loren-geofencing-zone.interface";
import {
  LorenExpansionPanelComponent
} from "../../../../shared/modules/loren-expansion-panel/components/loren-expansion-panel/loren-expansion-panel.component";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { LorenBaseMapFeature } from "../../../../shared/base-components/loren-base-map-feature/loren-base-map-feature";
import { LorenLayerService } from "../../../../shared/services/loren-layer-service/loren-layer.service";
import {
  LorenLayerUtilsService
} from "../../../../shared/services/loren-layer-service/services/loren-layer-utils-service/loren-layer-utils.service";
import { environment } from "../../../../../environments/environment";
import { LorenPrivilegeEntityType } from "../../../loren-roles/interfaces/loren-role.interface";
import { getAssetLocation } from "../../../asset/asset.utils";
import {getMapDefaults} from "../../../asset-map/map.defaults";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type Shape = Circle | Polygon | Rectangle;

@Component({
  selector: "loren-map-zones",
  templateUrl: "./loren-map-zones.component.html",
  styleUrls: ["./loren-map-zones.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenMapZonesComponent
  extends LorenBaseMapFeature
  implements OnInit, AfterViewInit
{
  protected lorenNavService: LorenNavService;
  dialog = inject(MatDialog);
  private lorenAssetService = inject(LorenAssetService);
  private activatedRoute = inject(ActivatedRoute);
  private geofencingCircleService = inject(LorenGeofencingCircleService);
  private changeDetector = inject(ChangeDetectorRef);
  private geofencingPolygonService = inject(LorenGeofencingPolygonService);
  private translateService = inject(TranslocoService);
  protected layerService: LorenLayerService;
  protected layerUtilsService: LorenLayerUtilsService;
  private lorenSnackBarService = inject(LorenSnackBarService);
  private ngZone = inject(NgZone);

  constructor() {
    const layerService = inject(LorenLayerService);
    const layerUtilsService = inject(LorenLayerUtilsService);

    super();

    this.layerService = layerService;
    this.layerUtilsService = layerUtilsService;
  }

  static FORM_NAME_FIELD = "name";
  static FORM_DESCRIPTION_FIELD = "description";
  static title = "features.geofencing";

  options = getMapDefaults();
  isSidenavOpened: boolean;
  initialDrawOfShapesDone: boolean;
  isDrawing: boolean;
  currentlyDrawnShapeType: LorenGeofencingType;
  circleZones: LorenGeofencingCircle[] = [];
  polygons: LorenGeofencingPolygon[] = [];
  zoneCirclesMap: Map<number, Circle> = new Map<number, Circle>();
  zonePolygonsMap: Map<number, Polygon> = new Map<number, Polygon>();
  zoneRectanglesMap: Map<number, Rectangle> = new Map<number, Rectangle>();
  zoneTooltipsMap: Map<number, Tooltip> = new Map<number, Tooltip>();
  zoneDeleteMarkersMap: Map<number, Marker> = new Map<number, Marker>();
  assetMarkerMap: Map<number, Marker> = new Map<number, Marker>();
  allZonesMap: Map<number, LorenGeofencingZone> = new Map<
    number,
    LorenGeofencingZone
  >();
  boundsPerZoneMap = new Map<number, LatLngBounds>();
  allZones: LorenGeofencingZone[] = [];
  drawOptions = LorenMap.drawOptions;
  currentlyDrawnShape;
  mapBounds = [];
  allShapesCount: number;
  shapeTypes = LorenGeofencingType;
  assets: LorenAsset[] = [];
  lastSelectedAsset: LorenAsset;
  polygonPointsDrawn = 0;
  listSelectorOpacity = 0.8;
  addButtonBottomOffset: number;
  lastEditedZoneLayers = new Map<number, Shape>();
  isEditing: boolean;
  tenant = environment.tenant;
  readonly privileges = LorenPrivilegeEntityType;

  @ViewChild("zonesPanel", { read: ElementRef, static: false })
  zonesPanelElement: ElementRef;
  @ViewChild("addZoneBtn", { read: ElementRef, static: false })
  addZoneBtnElement: ElementRef;
  @ViewChild(LorenExpansionPanelComponent)
  zonesPanelComponent: LorenExpansionPanelComponent;

  private static getPolygonZonePoints(zone: LorenGeofencingPolygon) {
    const polygonPoints = zone.polygonPoints;
    if (
      polygonPoints[0].latitude ===
        polygonPoints[polygonPoints.length - 1].latitude &&
      polygonPoints[0].longitude ===
        polygonPoints[polygonPoints.length - 1].longitude
    ) {
      polygonPoints.pop(); // first point duplicated as last on server
    }

    return polygonPoints;
  }

  private static createLeafletPolygon(zone: LorenGeofencingPolygon): Polygon {
    return polygon(
      LorenMapZonesComponent.getPolygonZonePoints(zone).map((x) => ({
        lat: x.latitude,
        lng: x.longitude,
      })),
      {
        color: LorenMap.drawOptions.draw.polygon.shapeOptions.color,
      }
    );
  }

  private static prepareFormModel(zone: LorenGeofencingZone = null) {
    const formModel: LorenEntityFormModel = {
      formFields: [
        {
          name: "name",
          value: !zone ? "" : zone.name,
          label: "geo_object.name",
          formControlType: LorenFieldTypes.INPUT,
        },
        {
          name: "description",
          value: !zone ? "" : zone.description,
          label: "geo_object.description",
          formControlType: LorenFieldTypes.TEXTAREA,
        },
      ],
      formName: "",
    };

    return formModel;
  }

  private static getShapeCenter(shape: Shape): LatLng {
    if (shape instanceof Circle) {
      return shape.getLatLng();
    } else {
      return shape.getBounds().getCenter();
    }
  }

  private static getShapeBounds(shape: Shape) {
    if (shape instanceof Circle) {
      return shape.getLatLng().toBounds(shape.getRadius() * 2);
    } else {
      return shape.getBounds();
    }
  }

  private static isDrawnObjectCircle(drawnObject): boolean {
    return drawnObject.options?.radius;
  }

  ngOnInit(): void {
    this.circleZones =
      this.activatedRoute.snapshot.data[LorenGeofencingResolver.circles];
    this.polygons =
      this.activatedRoute.snapshot.data[LorenGeofencingResolver.polygons];

    this.lorenNavService
        .onSidenavToggleStateChanged()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          this.isSidenavOpened = res;
        });

    this.prepareLeafletLabelsTranslation();

    this.allShapesCount = this.circleZones.length + this.polygons.length;
  }

  protected onMapLoaded(): void {
    this.mapInstance.on("draw:drawvertex", ($event) => {
      // Event handled when new points are drawn and existing removed
      // @ts-expect-error Baseline
      this.polygonPointsDrawn = Object.keys($event.layers._layers).length;
    });

    if (!this.initialDrawOfShapesDone) {
      this.preparePolygons();
      this.prepareRectangles();
      this.prepareCircleZones();
      this.allZones = Array.from(this.allZonesMap.values());

      if (this.allShapesCount > 0) {
        this.mapInstance.fitBounds(this.mapBounds);
      } else {
        this.setInitialLocation();
      }

      this.initialDrawOfShapesDone = true;
    }

    this.lorenAssetService
      .query()
      .pipe(take(1))
      .subscribe((x) => {
        this.assets = x.items;
        const subscriptions = this.assets.map((a) => {
            return this.lorenAssetService.getAssetImageObservable(a.image)
        });

        forkJoin(subscriptions)
          .pipe(take(1))
          .subscribe((res) => {
            this.assets.forEach((asset, index) => {
              this.assets[index].avatar = res[index];
            });

            this.prepareAssetsMarkers();
          });
      });
  }

  handleMoveResize(e) {
    const zone = this.allZonesMap.get(e.layer.zoneId);
    this.createTooltipForZone(zone, e.layer);
    this.removeZoneDeleteMarkerFromMap(zone.id);
  }

  handleDrawCreated(e) {
    const drawnObject = e.layer;
    if (drawnObject && drawnObject.editing) {
      drawnObject.editing.enable();
    }

    this.openSaveObjectModal(drawnObject);
  }

  openSaveObjectModal(drawnObject, zone: LorenGeofencingZone = null) {
    const dialogConfig = new MatDialogConfig();
    const formModel = LorenMapZonesComponent.prepareFormModel(zone);
    const modalData: LorenModalData = {
      form: formModel,
      modalAcceptBtnLabel: "operations.save",
      modalCloseBtnLabel: "operations.cancel",
      title: !zone
        ? "operations.create_geo_object"
        : "operations.update_geo_object",
      modalSubTitle: "operations.create_geo_object_sub_title",
      titleTranslationOptions: !zone ? null : { value: zone.name },
    };
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = modalData;

    const dialogRef = this.dialog.open(LorenDialogComponent, dialogConfig);

    dialogRef.afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          this.isDrawing = false;

          if (!result) {
            this.mapInstance.removeLayer(drawnObject);
            this.handleModalCancel(drawnObject, zone);
            return;
          }

          this.initiateZoneUpdate(drawnObject, zone, formModel);
          this.lorenSnackBarService.showSuccessMessage();
        });
  }

  cancelShapeCreation() {
    if (this.currentlyDrawnShape) {
      this.currentlyDrawnShape.disable();

      this.isDrawing = false;
    }
  }

  deleteLastPolygonPoint() {
    this.currentlyDrawnShape.deleteLastVertex();
  }

  finishPolygon() {
    this.currentlyDrawnShape.completeShape();
  }

  openShapeCreationDialog() {
    const dialogData: LorenGeofencingShape = {
      img: "",
      label: "",
    };

    this.zonesPanelComponent.collapse();

    this.prepareShapeCreationDialog(dialogData)
      .pipe(first())
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.currentlyDrawnShapeType = dialogData.type;
        this.isDrawing = true;
        switch (dialogData.type) {
          case LorenGeofencingType.CIRCLE: {
            this.currentlyDrawnShape = new L.Draw.Circle(
              // @ts-expect-error Baseline
              this.mapInstance,
              this.drawOptions.draw.circle
            );
            break;
          }
          case LorenGeofencingType.POLYGON: {
            this.currentlyDrawnShape = new L.Draw.Polygon(
              // @ts-expect-error Baseline
              this.mapInstance,
              this.drawOptions.draw.polygon
            );
            this.polygonPointsDrawn = 0;
            break;
          }
          case LorenGeofencingType.RECTANGLE: {
            this.currentlyDrawnShape = new L.Draw.Rectangle(
              // @ts-expect-error Baseline
              this.mapInstance,
              this.drawOptions.draw.rectangle
            );
            break;
          }
        }

        this.currentlyDrawnShape.enable();
      });
  }

  @HostListener("document:keydown.escape")
  onKeydownHandler() {
    this.cancelShapeCreation();
  }

  ngAfterViewInit() {
    setTimeout(() => this.calculateAddButtonBottomOffset(), 50);
  }

  changeListSelectorOpacity(opacityValue: number) {
    setTimeout(() => (this.listSelectorOpacity = opacityValue), 50);
  }

  calculateAddButtonBottomOffset() {
    this.addButtonBottomOffset =
      16 + this.zonesPanelElement.nativeElement.offsetHeight;
    this.handleLayerButtonBottomOffset();
  }

  protected getPanelHeight(): number {
    return (
      16 +
      this.zonesPanelElement.nativeElement.offsetHeight +
      this.addZoneBtnElement.nativeElement.offsetHeight
    );
  }

  fitBoundsToZone(zoneId: number) {
    const bounds = this.boundsPerZoneMap.get(zoneId);
    this.mapInstance.fitBounds(bounds);
    if (this.isHandset) {
      this.zonesPanelComponent.collapse();
    }
  }

  openDeleteModal(zone: LorenGeofencingZone) {
    const dialogConfig = new MatDialogConfig();
    const modalData: LorenModalData = {
      form: { formName: "", formFields: [] },
      modalAcceptBtnColor: "warn",
      modalAcceptBtnLabel: "operations.delete",
      modalCloseBtnLabel: "operations.cancel",
      title: "operations.delete_geo_object",
      modalSubTitle: null,
      titleTranslationOptions: { value: zone.name },
    };
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = modalData;

    const dialogRef = this.dialog.open(LorenDialogComponent, dialogConfig);

    dialogRef.afterClosed()
        .pipe(
            takeUntilDestroyed(this.destroyRef),
            switchMap(result => {
              if (!result) {
                return EMPTY;
              }

              if (zone.shape.type === LorenGeofencingType.CIRCLE) {
                return this.geofencingCircleService.del(zone.id);
              }

              return this.geofencingPolygonService.del(zone.id);
            })
        ).subscribe({
          next: () => {
            this.lorenSnackBarService.showSuccessMessage();
            this.removeZoneTooltipFromMap(zone.id);
            this.removeZoneDeleteMarkerFromMap(zone.id);
            this.removeZoneFromMap(zone.id);
          },
          error: (error) => {
            const errorStatus = error?.status || error?.error?.statusCode;

            if (errorStatus === 409) {
              // has rules attached to the geozone
              this.lorenSnackBarService.showTranslatedErrorMessage(
                  "operations.cannot_delete_geozone_with_rules"
              );
            } else {
              const errorMessage = error?.error?.message || error?.message || error?.error?.statusMessage || "operations.error_deleting_geozone";
              if (errorMessage.startsWith("operations.")) {
                this.lorenSnackBarService.showTranslatedErrorMessage(errorMessage);
              } else {
                this.lorenSnackBarService.showErrorMessage(errorMessage);
              }
            }
          }
        });
  }

  search(searchValue: string) {
    this.allZones = Array.from(this.allZonesMap.values()).filter((x) =>
      x.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }

  refreshAllZonesList() {
    this.allZones = Array.from(this.allZonesMap.values());
  }

  handleMapClick() {
    if (
      this.currentlyDrawnShapeType === LorenGeofencingType.CIRCLE &&
      this.isDrawing
    ) {
      this.isDrawing = false;
    }
  }

  navigateToMapMarker(asset: LorenAsset) {
    this.resetLastSelectedAssetMarkerState();
    this.lastSelectedAsset = asset;
    const assetMarker = this.assetMarkerMap.get(asset.id);
    if (assetMarker) {
      const dom = assetMarker
        .getElement()
        .getElementsByClassName("marker-pin")[0] as HTMLDivElement;
      dom.classList.add("active-marker-pin");
      this.mapInstance.setView(assetMarker.getLatLng(), 15);

      // assetMarker.togglePopup();
    }

    this.changeDetector.detectChanges();
  }

  cancelEdit() {
    this.isEditing = false;

    if (this.lastEditedZoneLayers.size > 0) {
      this.lastEditedZoneLayers.forEach((layer, zoneId) => {
        this.handleModalCancel(layer, this.allZonesMap.get(zoneId));
        this.mapInstance.removeLayer(layer);
      });

      this.lastEditedZoneLayers.clear();
    }
  }

  saveChanges() {
    this.isEditing = false;

    if (this.lastEditedZoneLayers.size > 0) {
      this.openConfirmationDialog({
        modalAcceptBtnColor: "accent",
        modalAcceptBtnLabel: "operations.save",
        modalCloseBtnLabel: "operations.cancel",
        title: "operations.confirm_all_changes",
        modalSubTitle: null,
      })
        .pipe(first())
        .subscribe((res) => {
          if (!res) {
            this.cancelEdit();
            return;
          }

          this.lastEditedZoneLayers.forEach((layer, zoneId) => {
            this.initiateZoneUpdate(layer, this.allZonesMap.get(zoneId));
          });

          this.lorenSnackBarService.showSuccessMessage();
          this.lastEditedZoneLayers.clear();
        });
    }
  }

  handleZoneEditFromList($event: LorenGeofencingZone) {
    const drawnObject =
      this.zonePolygonsMap.get($event.id) ||
      this.zoneCirclesMap.get($event.id) ||
      this.zoneRectanglesMap.get($event.id);

    this.openSaveObjectModal(drawnObject, $event);
  }

  private openConfirmationDialog(modalData: LorenModalData) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = modalData;

    return this.dialog.open(LorenDialogComponent, dialogConfig).afterClosed();
  }

  private initiateZoneUpdate(
    drawnObject,
    zone: LorenGeofencingZone,
    formModel?: LorenEntityFormModel
  ) {
    if (LorenMapZonesComponent.isDrawnObjectCircle(drawnObject)) {
      this.handleCircleCreateUpdate(
        drawnObject,
        zone as LorenGeofencingCircle,
        formModel
      );
    } else {
      this.handlePolygonCreateUpdate(
        drawnObject,
        zone as LorenGeofencingPolygon,
        formModel
      );
    }

    this.handleLastEditedZoneLayers(zone?.id);
  }

  private prepareAssetsMarkers() {
    const latLngBounds = [];

    this.assets.forEach((asset) => {
      const location = getAssetLocation(asset);
      if (!location) {
        return;
      }

      asset.streetViewLink = `${LorenMap.googleStreetViewLink}${location.lat},${location.lng}`;

      let mapMarker: Marker;
      if (!this.assetMarkerMap.has(asset.id)) {
        mapMarker = this.createNewMarker(location, asset);
        latLngBounds.push([
          mapMarker.getLatLng().lat,
          mapMarker.getLatLng().lng,
        ]);
        this.mapInstance.addLayer(mapMarker);
      } else {
        // update marker location
        mapMarker = this.assetMarkerMap.get(asset.id);
        mapMarker.setLatLng(location);
      }

      this.assetMarkerMap.set(asset.id, mapMarker);
    });
  }

  protected createNewMarker(lastLocation: LatLngLiteral, asset: LorenAsset) {
    const image = asset.avatar || "/icon-96x96.png";
    const icon = divIcon({
      className: "custom-div-icon",
      // html: "<div style='background-color:#c30b82;' class='marker-pin'></div><i class='material-icons'>weekend</i>",
      html: `<div class='marker-pin'></div><img src=${image}>`,
      iconSize: [30, 42],
      iconAnchor: [15, 42],
    });

    const mapMarker = marker(
      new LatLng(lastLocation.lat, lastLocation.lng),
      {
        icon,
      }
    );

    mapMarker.on("click", () => {
      this.navigateToMapMarker(asset);
    });

    mapMarker.bindPopup(`<h4>${asset.name}</h4>`, { autoClose: true });

    return mapMarker;
  }

  private resetLastSelectedAssetMarkerState() {
    if (this.lastSelectedAsset && this.lastSelectedAsset.location) {
      const lastMarker = this.assetMarkerMap.get(this.lastSelectedAsset.id);
      const dom = lastMarker
        .getElement()
        .getElementsByClassName("marker-pin")[0] as HTMLDivElement;
      dom.style.background = "";
      dom.classList.add("primary");
      dom.classList.remove("active-marker-pin");
      lastMarker.closePopup();
    }
  }

  private handleCircleCreateUpdate(
    drawnObject,
    zone: LorenGeofencingCircle,
    formModel: LorenEntityFormModel
  ) {
    const circleZone = this.transformDrawnObjectToCircleZone(
      drawnObject,
      formModel
    );
    const action = zone
      ? this.geofencingCircleService.patch(zone.id, circleZone)
      : this.geofencingCircleService.create(circleZone);

    action.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          this.mapInstance.removeLayer(drawnObject);
          circleZone.id = res.id;
          this.createMapCircleForZone(res);
        });
  }

  private handlePolygonCreateUpdate(
    drawnObject,
    zone: LorenGeofencingPolygon,
    formModel: LorenEntityFormModel
  ) {
    const existingZone = zone;
    const polygonType = existingZone
      ? existingZone.polygonType
      : this.currentlyDrawnShapeType;
    const polygonZone = this.transformDrawnObjectToPolygonZone(
      drawnObject,
      formModel,
      polygonType
    );
    const action = existingZone
      ? this.geofencingPolygonService.patch(zone.id, polygonZone)
      : this.geofencingPolygonService.create(polygonZone);


    action.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          this.mapInstance.removeLayer(drawnObject);
          polygonZone.id = res.id;
          if (polygonZone.polygonType === LorenGeofencingType.POLYGON) {
            this.createMapPolygonForZone(res);
          } else if (polygonZone.polygonType === LorenGeofencingType.RECTANGLE) {
            this.createMapRectangleForZone(res);
          }
        });
  }

  private handleModalCancel(drawnObject, zone: LorenGeofencingZone = null) {
    if (zone) {
      if (LorenMapZonesComponent.isDrawnObjectCircle(drawnObject)) {
        this.createMapCircleForZone(zone as LorenGeofencingCircle);
      } else {
        const polygonZone = zone as LorenGeofencingPolygon;
        if (polygonZone.polygonType === LorenGeofencingType.POLYGON) {
          this.createMapPolygonForZone(polygonZone);
        } else {
          this.createMapRectangleForZone(polygonZone);
        }
      }

      this.handleLastEditedZoneLayers(zone.id);
    }
  }

  private prepareLeafletLabelsTranslation() {
    const keys = [
      this.drawOptions.draw.polygon.drawError.message,
      this.drawOptions.draw.handlers.polygon.tooltip.start,
      this.drawOptions.draw.handlers.polygon.tooltip.cont,
      this.drawOptions.draw.handlers.polygon.tooltip.end,
      this.drawOptions.draw.handlers.circle.tooltip.start,
      this.drawOptions.draw.handlers.circle.radius,
      this.drawOptions.draw.handlers.rectangle.tooltip.start,
      this.drawOptions.draw.handlers.simpleshape.tooltip.end,
    ];

    const handlers = L.drawLocal.draw.handlers;

    this.translateService.selectTranslate(keys)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((translations) => {
          this.drawOptions.draw.polygon.drawError.message = translations[keys[0]];
          handlers.polygon.tooltip.start = translations[keys[1]];
          handlers.polygon.tooltip.cont = translations[keys[2]];
          handlers.polygon.tooltip.end = translations[keys[3]];
          handlers.circle.tooltip.start = translations[keys[4]];
          handlers.circle.radius = translations[keys[5]];
          handlers.rectangle.tooltip.start = translations[keys[6]];
          handlers.simpleshape.tooltip.end = translations[keys[7]];
        });
  }

  private prepareShapeCreationDialog(dialogData) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.maxWidth = "800px";
    dialogConfig.maxHeight = "600px";
    dialogConfig.data = dialogData;

    const dialogRef = this.dialog.open(
      LorenShapeCreationDialogComponent,
      dialogConfig
    );

    return dialogRef.afterClosed();
  }

  private transformDrawnObjectToCircleZone(
    drawnObject,
    formModel: LorenEntityFormModel
  ) {
    const description = formModel?.formFields.find(
      (x) => x.name === LorenMapZonesComponent.FORM_DESCRIPTION_FIELD
    ).value;
    const name = formModel?.formFields.find(
      (x) => x.name === LorenMapZonesComponent.FORM_NAME_FIELD
    ).value;
    const circleZone: LorenGeofencingCircle = {
      center: {
        latitude: drawnObject._latlng.lat,
        longitude: drawnObject._latlng.lng,
      },
      radius: drawnObject.getRadius(),
    };

    if (name) {
      circleZone.name = name;
    }

    if (description) {
      circleZone.description = description;
    }

    return circleZone;
  }

  private transformDrawnObjectToPolygonZone(
    drawnObject,
    formModel: LorenEntityFormModel,
    polygonType: LorenGeofencingType
  ) {
    const drawnPoints: LatLng[] = drawnObject._latlngs[0];
    // duplicate first point as last (server requirement)
    drawnPoints.push(drawnPoints[0]);

    const description = formModel?.formFields.find(
      (x) => x.name === LorenMapZonesComponent.FORM_DESCRIPTION_FIELD
    ).value;
    const name = formModel?.formFields.find(
      (x) => x.name === LorenMapZonesComponent.FORM_NAME_FIELD
    ).value;

    const polygonZone: LorenGeofencingPolygon = {
      polygonPoints: drawnPoints.map((x) => ({
        latitude: x.lat,
        longitude: x.lng,
      })),
      polygonType,
    };

    if (name) {
      polygonZone.name = name;
    }

    if (description) {
      polygonZone.description = description;
    }

    return polygonZone;
  }

  private preparePolygons() {
    this.polygons
      .filter((p) => p.polygonType === LorenGeofencingType.POLYGON)
      .forEach((zone) => {
        this.createMapPolygonForZone(zone);
        this.mapBounds.push(this.boundsPerZoneMap.get(zone.id));
      });
  }

  private prepareRectangles() {
    this.polygons
      .filter((p) => p.polygonType === LorenGeofencingType.RECTANGLE)
      .forEach((zone) => {
        this.createMapRectangleForZone(zone);
        this.mapBounds.push(this.boundsPerZoneMap.get(zone.id));
      });
  }

  private prepareCircleZones() {
    this.circleZones.forEach((zone) => {
      this.createMapCircleForZone(zone);
      this.mapBounds.push(this.boundsPerZoneMap.get(zone.id));
    });
  }

  private initializeShapeOnMap(
    zone: LorenGeofencingCircle | LorenGeofencingPolygon,
    shape: Shape
  ) {
    // @ts-expect-error Baseline
    shape.zoneId = zone.id;

    shape.editing.enable();

    shape.on("edit", ($event) => {
      const editedShape = $event.target;
      this.isEditing = true;
      this.lastEditedZoneLayers.set(zone.id, editedShape);
      this.createDeleteMarkerForZone(zone, editedShape);
      this.createTooltipForZone(zone, editedShape);
    });

    this.createTooltipForZone(zone, shape);
    this.createDeleteMarkerForZone(zone, shape);
    this.allZonesMap.set(zone.id, zone);
    this.refreshAllZonesList();
    this.boundsPerZoneMap.set(
      zone.id,
      LorenMapZonesComponent.getShapeBounds(shape)
    );
    this.mapInstance.addLayer(shape);
  }

  private handleMoveEndForShape(
    zone: LorenGeofencingCircle | LorenGeofencingPolygon,
    shape: Circle | Rectangle
  ) {
    shape.on("moveend", ($event) => {
      this.createDeleteMarkerForZone(zone, $event.target);
    });
  }

  private createMapRectangleForZone(zone: LorenGeofencingPolygon): Rectangle {
    const polygonTemp = LorenMapZonesComponent.createLeafletPolygon(zone);
    const mapRectangle = rectangle(polygonTemp.getBounds(), {
      color: LorenMap.drawOptions.draw.polygon.shapeOptions.color,
    });

    zone.shape = LorenGeofencingShapes.find(
      (x) => x.type === LorenGeofencingType.RECTANGLE
    );

    this.handleMoveEndForShape(zone, mapRectangle);
    this.initializeShapeOnMap(zone, mapRectangle);
    this.zoneRectanglesMap.set(zone.id, mapRectangle);

    return mapRectangle;
  }

  private createMapPolygonForZone(zone: LorenGeofencingPolygon): Polygon {
    const mapPolygon = LorenMapZonesComponent.createLeafletPolygon(zone);

    zone.shape = LorenGeofencingShapes.find(
      (x) => x.type === LorenGeofencingType.POLYGON
    );

    // Polygons cannot be moved by default if not for some custom implementation
    // https://github.com/Leaflet/Leaflet.draw/issues/243
    this.initializeShapeOnMap(zone, mapPolygon);
    this.zonePolygonsMap.set(zone.id, mapPolygon);

    return mapPolygon;
  }

  private createMapCircleForZone(zone: LorenGeofencingCircle): Circle {
    const mapCircle = circle(
      { lat: zone.center.latitude, lng: zone.center.longitude },
      { radius: zone.radius, color: LorenMap.circleColor }
    );

    zone.shape = LorenGeofencingShapes.find(
      (x) => x.type === LorenGeofencingType.CIRCLE
    );

    this.handleMoveEndForShape(zone, mapCircle);
    this.initializeShapeOnMap(zone, mapCircle);
    this.zoneCirclesMap.set(zone.id, mapCircle);

    return mapCircle;
  }

  private createDeleteMarkerForZone(
    zone: LorenGeofencingCircle | LorenGeofencingPolygon,
    shape: Shape
  ) {
    const icon = divIcon({
      className: "delete-zone-icon mt-2",
      iconSize: [15, 15],
      html: `<span class="material-icons warn">delete</span>`,
    });

    const shapeCenter = LorenMapZonesComponent.getShapeCenter(shape);
    const mapMarker = marker(shapeCenter, {
      icon,
    });

    this.removeZoneDeleteMarkerFromMap(zone.id);

    mapMarker.on("click", () => {
      this.ngZone.run(() => {
        this.openDeleteModal(zone);
      });
    });

    mapMarker.addTo(this.mapInstance);
    this.zoneDeleteMarkersMap.set(zone.id, mapMarker);

    return mapMarker;
  }

  private createTooltipForZone(
    zone: LorenGeofencingZone,
    shape: Shape
  ) {
    const shapeCenter = LorenMapZonesComponent.getShapeCenter(shape);
    const shapeTooltip = tooltip({
      direction: "center",
      className: "text",
      permanent: true,

      // TODO: fix
      // zoomAnimation: true,
      interactive: true,
    })
      .setContent(zone.name)
      .setLatLng(shapeCenter);

    this.removeZoneTooltipFromMap(zone.id);

    shapeTooltip.addTo(this.mapInstance);
    this.zoneTooltipsMap.set(zone.id, shapeTooltip);
    return shapeTooltip;
  }

  private removeZoneDeleteMarkerFromMap(zoneId: number) {
    if (this.zoneDeleteMarkersMap.has(zoneId)) {
      this.mapInstance.removeLayer(this.zoneDeleteMarkersMap.get(zoneId));
      this.zoneDeleteMarkersMap.delete(zoneId);
    }
  }

  private removeZoneTooltipFromMap(zoneId: number) {
    if (this.zoneTooltipsMap.has(zoneId)) {
      this.mapInstance.removeLayer(this.zoneTooltipsMap.get(zoneId));
      this.zoneTooltipsMap.delete(zoneId);
    }
  }

  private removeZoneFromMap(zoneId) {
    let zone;

    if (this.zoneCirclesMap.has(zoneId)) {
      zone = this.zoneCirclesMap.get(zoneId);
      this.zoneCirclesMap.delete(zoneId);
    }

    if (this.zonePolygonsMap.has(zoneId)) {
      zone = this.zonePolygonsMap.get(zoneId);
      this.zonePolygonsMap.delete(zoneId);
    }

    if (this.zoneRectanglesMap.has(zoneId)) {
      zone = this.zoneRectanglesMap.get(zoneId);
      this.zoneRectanglesMap.delete(zoneId);
    }

    if (zone) {
      this.mapInstance.removeLayer(zone);
      this.allZonesMap.delete(zoneId);
    }

    this.refreshAllZonesList();
  }

  private handleLastEditedZoneLayers(zoneId: number) {
    if (this.lastEditedZoneLayers.has(zoneId)) {
      this.lastEditedZoneLayers.delete(zoneId);
    }
  }
}
